/**
 * Activity Item — checkbox + detail row for the activities list.
 */

import { useNavigate } from 'react-router-dom'
import { CheckSquare, Square, Phone, Pencil, Calendar, User, TrendingUp, AlertTriangle } from 'lucide-react'
import type { Activity } from '@/types/crm'

interface ActivityItemProps {
  activity: Activity
  onComplete: (id: string) => void
}

const TYPE_ICONS: Record<string, React.ElementType> = {
  task: CheckSquare,
  note: Pencil,
  call: Phone,
  meeting: Calendar,
}

const PRIORITY_STYLES: Record<string, string> = {
  urgent: 'border-l-destructive',
  high: 'border-l-warning',
  normal: 'border-l-transparent',
  low: 'border-l-border',
}

export function ActivityItem({ activity, onComplete }: ActivityItemProps) {
  const navigate = useNavigate()
  const Icon = TYPE_ICONS[activity.activityType] || CheckSquare
  const isOverdue = !activity.isCompleted && activity.dueDate && new Date(activity.dueDate) < new Date()

  const formatDueDate = () => {
    if (!activity.dueDate) return null
    const due = new Date(activity.dueDate)
    const now = new Date()
    const diffMs = due.getTime() - now.getTime()
    const diffDays = Math.floor(diffMs / 86400000)

    if (activity.isCompleted) {
      return due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    }
    if (diffDays < -1) return `${Math.abs(diffDays)}d overdue`
    if (diffDays === -1) return 'Yesterday'
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays < 7) return due.toLocaleDateString(undefined, { weekday: 'short' })
    return due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  const dueLabel = formatDueDate()

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg hover:bg-muted border-l-2 transition-colors ${
      PRIORITY_STYLES[activity.priority] || PRIORITY_STYLES.normal
    } ${activity.isCompleted ? 'opacity-60' : ''}`}>
      {/* Checkbox (only for tasks) */}
      {activity.activityType === 'task' ? (
        <button
          onClick={(e) => { e.stopPropagation(); if (!activity.isCompleted) onComplete(activity.id) }}
          className="mt-0.5 shrink-0"
          disabled={activity.isCompleted}
        >
          {activity.isCompleted ? (
            <CheckSquare className="w-4 h-4 text-success" />
          ) : (
            <Square className="w-4 h-4 text-muted-foreground hover:text-primary" />
          )}
        </button>
      ) : (
        <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium leading-snug ${
            activity.isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'
          }`}>
            {activity.title || `${activity.activityType} logged`}
          </p>

          {dueLabel && (
            <span className={`text-xs shrink-0 flex items-center gap-1 ${
              isOverdue ? 'text-destructive font-medium' : 'text-muted-foreground'
            }`}>
              {isOverdue && <AlertTriangle className="w-3 h-3" />}
              {dueLabel}
            </span>
          )}
        </div>

        {activity.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{activity.description}</p>
        )}

        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          {activity.contactName && (
            <button
              onClick={() => navigate(`/crm/contacts/${activity.contactId}`)}
              className="flex items-center gap-0.5 hover:text-primary"
            >
              <User className="w-3 h-3" />{activity.contactName}
            </button>
          )}
          {activity.dealName && (
            <span className="flex items-center gap-0.5">
              <TrendingUp className="w-3 h-3" />{activity.dealName}
            </span>
          )}
          {activity.isCompleted && activity.completedAt && (
            <span>
              Completed {new Date(activity.completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
